import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FileStack,
  FileText,
  MessageCircle,
  MoreHorizontal,
  NotebookText,
  Pin,
  PinOff,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { GLASS_INPUT, MENU_CONTENT, MENU_ITEM_BASE } from '@/shared/ui-class';
import { projectTileBySlug, PROJECT_TILES } from '@/versions/v1/shared/project-seed';
import {
  PROJECT_SIDEBAR_NAV_ITEMS,
  isChatThreadListPath,
  isProjectChatPath,
  isProjectSidebarNavActive,
  parseProjectSlugFromPathname,
  projectSidebarHref,
  resolveProjectsSidebarHeader,
} from '@/versions/v1/shared/project-sidebar-nav';
import { useChat } from '@/versions/v1/shared/ChatContext';
import type { ChatThread } from '@/versions/v1/shared/chat-types';
import {
  SIDEBAR_SECTION_LABEL,
  SidebarRail,
  SidebarRailButton,
  SidebarRow,
  SidebarShell,
} from './SidebarShell';
import { SidebarSceneHeader } from './SidebarSceneHeader';
import { SidebarToggle } from './SidebarToggle';
import { AccountMenu, useClickOutside } from './AccountMenu';
import { ConfirmModal } from '@/versions/v1/modules/admin/ConfirmModal';
import { BrandMark } from '@/versions/v1/shared/BrandMark'; // fork(oem): brand-aware wordmark

// ThreadHistorySidebar — the workspace/projects rail (parity.P1, recomposed
// in parity.P5 to match ai-foundation main's per-route sidebar model):
//   `/` + other workspace surfaces → wordmark header · Workspace quick-nav
//     · search · Pinned · Recents. (No projects list — ai-foundation removed
//     it in 27adfc6b; projects are reached via the Workspace "Projects" row.)
//   `/projects` (portfolio)        → "Portfolio" scene header (back to
//     workspace) · Workspace quick-nav only.
//   `/projects/<slug>/…`           → project-name scene header (back to
//     portfolio / project) · Modules nav · footer back-row. No global threads.
//   `/projects/<slug>/chat`        → adds search + the project's own
//     Pinned/Recents thread lists.

type ThreadRow = Pick<ChatThread, 'id' | 'title' | 'pinned' | 'updatedAt' | 'projectSlug'>;

const WORKSPACE_NAV = [
  { href: '/', label: 'AI Assistant', Icon: MessageCircle },
  { href: '/projects', label: 'Projects', Icon: FileStack },
  { href: '/docs/logic', label: 'Build docs', Icon: NotebookText },
  { href: '/changelog', label: 'Changelog', Icon: FileText },
] as const;

function ThreadOverflowMenu({
  pinned,
  projectSlug,
  onTogglePin,
  onDelete,
  onMoveToProject,
  onMoveOut,
}: {
  pinned: boolean;
  projectSlug?: string;
  onTogglePin: () => void;
  onDelete: () => void;
  onMoveToProject: (slug: string) => void;
  onMoveOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    setOpen(false);
    setMoveOpen(false);
  }, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="More options"
        title="More options"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-900/5 hover:text-zinc-900 group-hover:opacity-100 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
      >
        <MoreHorizontal className="h-4 w-4" strokeWidth={1.6} />
      </button>
      {open ? (
        <div
          className={cn(MENU_CONTENT, 'absolute right-0 top-full z-50 mt-1 min-w-[180px]')}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className={MENU_ITEM_BASE}
            onClick={() => {
              onTogglePin();
              setOpen(false);
            }}
          >
            {pinned ? (
              <PinOff className="h-4 w-4 text-zinc-500" />
            ) : (
              <Pin className="h-4 w-4 text-zinc-500" />
            )}
            {pinned ? 'Unpin' : 'Pin'}
          </button>
          <div className="relative">
            <button
              type="button"
              className={cn(MENU_ITEM_BASE, 'w-full justify-between')}
              onClick={() => setMoveOpen((value) => !value)}
            >
              Move to project
              <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
            </button>
            {moveOpen ? (
              <div
                className={cn(
                  MENU_CONTENT,
                  'absolute left-full top-0 z-50 ml-1 min-w-[160px]',
                )}
              >
                {PROJECT_TILES.map((project) => (
                  <button
                    key={project.slug}
                    type="button"
                    className={MENU_ITEM_BASE}
                    onClick={() => {
                      onMoveToProject(project.slug);
                      setOpen(false);
                      setMoveOpen(false);
                    }}
                  >
                    {project.name}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {projectSlug ? (
            <button
              type="button"
              className={MENU_ITEM_BASE}
              onClick={() => {
                onMoveOut();
                setOpen(false);
              }}
            >
              Move out of project
            </button>
          ) : null}
          <div className="my-1 h-px bg-zinc-200 dark:bg-zinc-800" />
          <button
            type="button"
            className="flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm text-red-600/80 outline-none hover:text-red-700 focus:bg-red-500/5 dark:text-red-500/80"
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ThreadRowItem({
  thread,
  isActive,
  onSelect,
  onTogglePin,
  onRequestDelete,
  onMoveToProject,
  onMoveOut,
}: {
  thread: ThreadRow;
  isActive: boolean;
  onSelect: (id: string) => void;
  onTogglePin: () => void;
  onRequestDelete: () => void;
  onMoveToProject: (slug: string) => void;
  onMoveOut: () => void;
}) {
  return (
    <li
      className={cn(
        'group relative mx-2 flex min-h-[32px] items-center rounded-lg transition-colors',
        isActive
          ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,transparent)]'
          : 'hover:bg-zinc-900/5 dark:hover:bg-white/5',
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(thread.id)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3 py-2 text-left"
      >
        <MessageCircle
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0',
            isActive
              ? 'text-accent-700 dark:text-accent-400'
              : 'text-zinc-400 dark:text-zinc-500',
          )}
          strokeWidth={1.6}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-sm leading-[1.35] tracking-[-0.003em] text-zinc-900 dark:text-zinc-100">
          {thread.title}
        </span>
      </button>
      <div className="pr-1">
        <ThreadOverflowMenu
          pinned={thread.pinned}
          projectSlug={thread.projectSlug}
          onTogglePin={onTogglePin}
          onDelete={onRequestDelete}
          onMoveToProject={onMoveToProject}
          onMoveOut={onMoveOut}
        />
      </div>
    </li>
  );
}

function CollapsibleThreadSection({
  label,
  threads,
  activeThreadId,
  emptyHint,
  onSelect,
  onTogglePin,
  onRequestDelete,
  onMoveToProject,
  onMoveOut,
}: {
  label: string;
  threads: ThreadRow[];
  activeThreadId: string | null;
  emptyHint?: string;
  onSelect: (id: string) => void;
  onTogglePin: (id: string) => void;
  onRequestDelete: (id: string) => void;
  onMoveToProject: (id: string, slug: string) => void;
  onMoveOut: (id: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (threads.length === 0 && !emptyHint) return null;

  return (
    <div>
      <div className="mb-2 mt-6 px-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex w-full items-center justify-between"
        >
          <span className={SIDEBAR_SECTION_LABEL}>{label}</span>
          {collapsed ? (
            <ChevronRight className="h-3 w-3 text-zinc-400" strokeWidth={2} />
          ) : (
            <ChevronDown className="h-3 w-3 text-zinc-400" strokeWidth={2} />
          )}
        </button>
      </div>
      {!collapsed ? (
        threads.length === 0 ? (
          <p className="px-3 text-xs leading-relaxed text-muted">{emptyHint}</p>
        ) : (
          <ul className="space-y-0">
            {threads.map((t) => (
              <ThreadRowItem
                key={t.id}
                thread={t}
                isActive={t.id === activeThreadId}
                onSelect={onSelect}
                onTogglePin={() => onTogglePin(t.id)}
                onRequestDelete={() => onRequestDelete(t.id)}
                onMoveToProject={(slug) => onMoveToProject(t.id, slug)}
                onMoveOut={() => onMoveOut(t.id)}
              />
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export function ThreadHistorySidebar() {
  const { pathname } = useLocation();
  const {
    threads,
    currentThreadId,
    selectThread,
    deleteThread,
    toggleThreadPin,
    moveThreadToProject,
    moveThreadOut,
  } = useChat();
  const [searchQuery, setSearchQuery] = useState('');
  const [workspaceCollapsed, setWorkspaceCollapsed] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const projectSlug = parseProjectSlugFromPathname(pathname);
  const onProjectChat = isProjectChatPath(pathname);
  const activeProject = projectTileBySlug(projectSlug ?? undefined);

  // ai-foundation: conversation search belongs to chat surfaces — `/`
  // (plus the mock's other workspace surfaces) and the project assistant.
  const showSearch = isChatThreadListPath(pathname);
  const showThreads = showSearch;

  const headerMeta = resolveProjectsSidebarHeader(
    pathname,
    activeProject?.name ?? null,
  );

  const scopedThreads = useMemo(() => {
    if (onProjectChat && projectSlug) {
      return threads.filter((t) => t.projectSlug === projectSlug);
    }
    // Workspace chat-home (`/`) — route owns scope; exclude project threads.
    return threads.filter((t) => !t.projectSlug);
  }, [threads, onProjectChat, projectSlug]);

  const filteredAll = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return scopedThreads.filter((t) => t.title.toLowerCase().includes(q));
  }, [searchQuery, scopedThreads]);

  const { pinned, recents } = useMemo(() => {
    const pinnedRows = scopedThreads.filter((t) => t.pinned);
    const recentsRows = scopedThreads.filter((t) => !t.pinned);
    return { pinned: pinnedRows, recents: recentsRows };
  }, [scopedThreads]);

  const conversationSearch = showSearch ? (
    <div className="px-2.5 pb-0.5 pt-4">
      <div className="relative h-8">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
        <input
          ref={searchInputRef}
          type="search"
          aria-label="Search conversations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search conversations…"
          className={cn(
            'peer h-8 w-full rounded-md pl-[30px] text-sm font-normal text-zinc-900 outline-none transition-colors placeholder:font-light placeholder:text-zinc-400 focus:border-[var(--glass-border-strong)] dark:text-zinc-100 dark:placeholder:text-zinc-500',
            GLASS_INPUT,
            searchQuery ? 'pr-8' : 'pr-12',
            '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
          )}
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-zinc-400 transition-colors hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-200"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden />
          </button>
        ) : (
          <span
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'transition-opacity duration-150 peer-focus:opacity-0',
            )}
            title="⌘K opens search"
          >
            <kbd
              aria-hidden
              className={cn(
                'pointer-events-none block rounded border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-1.5 py-px',
                'font-mono text-xs font-medium leading-none text-zinc-400',
                'dark:text-zinc-500',
              )}
            >
              ⌘K
            </kbd>
          </span>
        )}
      </div>
    </div>
  ) : null;

  const collapsedRail = (
    <SidebarRail>
      {projectSlug
        ? PROJECT_SIDEBAR_NAV_ITEMS.map((item) => (
              <SidebarRailButton
                key={item.segment}
                href={projectSidebarHref(projectSlug, item.segment)}
                icon={item.Icon}
                label={item.label}
                isActive={isProjectSidebarNavActive(
                  pathname,
                  projectSlug,
                  item.segment,
                )}
              />
            ))
        : WORKSPACE_NAV.map((item) => (
            <SidebarRailButton
              key={item.href}
              href={item.href}
              icon={item.Icon}
              label={item.label}
              isActive={
                pathname === item.href ||
                (item.href !== '/' && pathname.startsWith(`${item.href}/`))
              }
            />
          ))}
    </SidebarRail>
  );

  return (
    <Commentable name="ThreadHistorySidebar">
      <SidebarShell
        ariaLabel={projectSlug ? 'Project navigation' : 'Chat history'}
        collapsedRail={collapsedRail}
        header={
          headerMeta ? (
            <SidebarSceneHeader title={headerMeta.title} back={headerMeta.back} />
          ) : (
            <>
              {/* fork(oem): was the literal "Foundation" */}
              <BrandMark variant="wordmark" className="min-w-0 truncate text-sm" />
              <SidebarToggle variant="sidebar" />
            </>
          )
        }
        footer={
          projectSlug ? (
            <>
              <SidebarRow href="/projects" icon={ArrowLeft} label="Projects" />
              <AccountMenu />
            </>
          ) : undefined
        }
      >
        {!projectSlug ? (
          <Commentable name="Workspace">
            <nav aria-label="Workspace quick navigation">
              <div className="mb-2 mt-3 px-3">
                <button
                  type="button"
                  onClick={() => setWorkspaceCollapsed((v) => !v)}
                  aria-expanded={!workspaceCollapsed}
                  className="flex w-full cursor-pointer items-center justify-between rounded transition-[background-color,box-shadow] hover:bg-zinc-900/[0.04] hover:[box-shadow:0_0_0_0.5px_rgb(0_0_0/0.04)] dark:hover:bg-white/[0.04] dark:hover:[box-shadow:0_0_0_0.5px_rgb(255_255_255/0.04)]"
                >
                  <span className={SIDEBAR_SECTION_LABEL}>Workspace</span>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-zinc-400">
                    {workspaceCollapsed ? (
                      <ChevronRight className="h-3 w-3" strokeWidth={2} />
                    ) : (
                      <ChevronDown className="h-3 w-3" strokeWidth={2} />
                    )}
                  </span>
                </button>
              </div>
              {!workspaceCollapsed ? (
                <ul className="space-y-0">
                  {WORKSPACE_NAV.map((item) => (
                    <li key={item.href}>
                      <SidebarRow
                        href={item.href}
                        icon={item.Icon}
                        label={item.label}
                        isActive={
                          pathname === item.href ||
                          (item.href !== '/' &&
                            pathname.startsWith(`${item.href}/`))
                        }
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </nav>
          </Commentable>
        ) : null}

        {projectSlug ? (
          <Commentable name="Modules">
            <nav aria-label="Modules navigation">
              <div className="mb-2 mt-3 px-3">
                <span className={SIDEBAR_SECTION_LABEL}>Modules</span>
              </div>
              <ul className="space-y-0">
                {PROJECT_SIDEBAR_NAV_ITEMS.map((item) => (
                  <li key={item.segment}>
                    <SidebarRow
                      href={projectSidebarHref(projectSlug, item.segment)}
                      icon={item.Icon}
                      label={item.label}
                      isActive={isProjectSidebarNavActive(
                        pathname,
                        projectSlug,
                        item.segment,
                      )}
                    />
                  </li>
                ))}
              </ul>
            </nav>
          </Commentable>
        ) : null}

        {conversationSearch}

        {showThreads ? (
          filteredAll !== null ? (
            filteredAll.length === 0 ? (
              <div className="mt-6 px-3 text-center text-xs text-muted">
                No conversations match &ldquo;{searchQuery}&rdquo;.
              </div>
            ) : (
              <div>
                <div className="mb-2 mt-6 px-3">
                  <span className={SIDEBAR_SECTION_LABEL}>
                    {filteredAll.length}{' '}
                    {filteredAll.length === 1 ? 'result' : 'results'}
                  </span>
                </div>
                <ul className="space-y-0">
                  {filteredAll.map((t) => (
                    <ThreadRowItem
                      key={t.id}
                      thread={t}
                      isActive={t.id === currentThreadId}
                      onSelect={selectThread}
                      onTogglePin={() => toggleThreadPin(t.id)}
                      onRequestDelete={() => setPendingDeleteId(t.id)}
                      onMoveToProject={(slug) => moveThreadToProject(t.id, slug)}
                      onMoveOut={() => moveThreadOut(t.id)}
                    />
                  ))}
                </ul>
              </div>
            )
          ) : (
            <>
              {pinned.length > 0 ? (
                <Commentable name="Pinned">
                  <CollapsibleThreadSection
                    label="Pinned"
                    threads={pinned}
                    activeThreadId={currentThreadId}
                    onSelect={selectThread}
                    onTogglePin={toggleThreadPin}
                    onRequestDelete={setPendingDeleteId}
                    onMoveToProject={moveThreadToProject}
                    onMoveOut={moveThreadOut}
                  />
                </Commentable>
              ) : null}

              <Commentable name="Recents">
                <CollapsibleThreadSection
                  label="Recents"
                  threads={recents}
                  activeThreadId={currentThreadId}
                  emptyHint={
                    onProjectChat
                      ? 'No chats in this project yet — start one from the composer.'
                      : undefined
                  }
                  onSelect={selectThread}
                  onTogglePin={toggleThreadPin}
                  onRequestDelete={setPendingDeleteId}
                  onMoveToProject={moveThreadToProject}
                  onMoveOut={moveThreadOut}
                />
              </Commentable>
            </>
          )
        ) : null}
      </SidebarShell>

      <ConfirmModal
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        title="Delete conversation?"
        /* fork(oem/P7a C4): "proof of concept", never "mock" — meeting decision,
           22 Sep 2026. Externally facing copy only; the mechanism is upstream. */
        description="This removes the thread from your history in this proof of concept."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={() => {
          if (pendingDeleteId) deleteThread(pendingDeleteId);
        }}
      />
    </Commentable>
  );
}
